import { ProfessionalProfileUseCase } from '../usecases/professional-profile.usecase';
import { AnalyticsUseCase } from '../usecases/analytics.usecase';
import { CreateProfessionalProfileData, UpdateProfessionalProfileData, GetProfessionalProfileData, ProfessionalProfileFilter } from '../repository/professional-profile.repository';

export class ProfessionalProfileService {
  constructor(
    private professionalProfileUseCase: ProfessionalProfileUseCase,
    private analyticsUseCase?: AnalyticsUseCase
  ) {
    console.log('[ProfessionalProfileService] Constructor - Professional profile use case initialized:', !!this.professionalProfileUseCase);
  }

  // ===== PROFILE MANAGEMENT =====

  async createProfessionalProfile(profileData: CreateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.createProfessionalProfile] START - ProfileData:', profileData);
    try {
      const profile = await this.professionalProfileUseCase.createProfessionalProfile(profileData);

      // Track analytics event - commented out until trackEvent is implemented
      // if (this.analyticsUseCase) {
      //   await this.analyticsUseCase.trackEvent({
      //     eventType: 'professionalProfileCreated',
      //     eventData: {
      //       profileId: profile.id,
      //       userId: profile.userId,
      //       specialties: profile.specialties,
      //       yearsOfExperience: profile.yearsOfExperience,
      //       verificationStatus: profile.verificationStatus,
      //       profileVisibility: profile.profileVisibility
      //     },
      //     userId: profile.userId
      //   });
      // }

      console.log('[ProfessionalProfileService.createProfessionalProfile] SUCCESS - Profile created:', profile.id);
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.createProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  async getProfessionalProfileById(profileId: string): Promise<GetProfessionalProfileData | null> {
    console.log('[ProfessionalProfileService.getProfessionalProfileById] START - ProfileId:', profileId);
    try {
      const profile = await this.professionalProfileUseCase.getProfessionalProfileById(profileId);
      console.log('[ProfessionalProfileService.getProfessionalProfileById] SUCCESS - Profile found:', !!profile);
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.getProfessionalProfileById] ERROR:', error);
      throw error;
    }
  }

  async getProfessionalProfiles(filter: ProfessionalProfileFilter = {}, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getProfessionalProfiles] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.getProfessionalProfiles(filter, limitCount);
      console.log('[ProfessionalProfileService.getProfessionalProfiles] SUCCESS - Retrieved', profiles.length, 'profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.getProfessionalProfiles] ERROR:', error);
      throw error;
    }
  }

  async getAllProfessionalProfiles(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getAllProfessionalProfiles] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.getAllProfessionalProfiles(limitCount);
      console.log('[ProfessionalProfileService.getAllProfessionalProfiles] SUCCESS - Retrieved', profiles.length, 'profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.getAllProfessionalProfiles] ERROR:', error);
      throw error;
    }
  }

  async updateProfessionalProfile(profileId: string, updateData: UpdateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.updateProfessionalProfile] START - ProfileId:', profileId, 'UpdateData:', updateData);
    try {
      const updatedProfile = await this.professionalProfileUseCase.updateProfessionalProfile(profileId, updateData);

      // Invalidate every React Query cache that holds professional-profile
      // data so the UI rerenders with the fresh values immediately. Without
      // this the dashboard profile page (`['professionalProfile', uid]`)
      // and the navbar avatar (same key) keep showing the pre-update state
      // for up to 5 minutes (queryClient.staleTime), which is the "I edited
      // my profile but nothing changed" complaint.
      try {
        const { queryClient } = await import('@/lib/queryClient');
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['professionalProfile'] }),
          queryClient.invalidateQueries({ queryKey: ['professionalProfiles'] }),
          queryClient.invalidateQueries({ queryKey: ['userProfile', updatedProfile.userId] }),
          queryClient.invalidateQueries({ queryKey: ['professional', updatedProfile.userId] }),
        ]);
      } catch (cacheError) {
        console.warn('[ProfessionalProfileService.updateProfessionalProfile] cache invalidation failed:', cacheError);
      }

      // Track analytics event for significant updates
      if (this.analyticsUseCase) {
        const significantFields = ['verificationStatus', 'profileVisibility', 'isAvailable', 'isAcceptingStudents'];
        const hasSignificantUpdate = significantFields.some(field => updateData.hasOwnProperty(field));

        if (hasSignificantUpdate) {
          // analytics tracking placeholder
        }
      }

      console.log('[ProfessionalProfileService.updateProfessionalProfile] SUCCESS - Profile updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileService.updateProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  async deleteProfessionalProfile(profileId: string): Promise<void> {
    console.log('[ProfessionalProfileService.deleteProfessionalProfile] START - ProfileId:', profileId);
    try {
      // Get profile info before deletion for analytics
      const profile = await this.professionalProfileUseCase.getProfessionalProfileById(profileId);
      
      await this.professionalProfileUseCase.deleteProfessionalProfile(profileId);

      // Track analytics event
      if (this.analyticsUseCase && profile) {
        // await this.analyticsUseCase.trackEvent({
        //   eventType: 'professionalProfileDeleted',
        //   eventData: {
        //     profileId: profileId,
        //     verificationStatus: profile.verificationStatus,
        //     totalRequests: profile.totalRequests,
        //     averageRating: profile.averageRating
        //   },
        //   userId: profile.userId
        // });
      }

      console.log('[ProfessionalProfileService.deleteProfessionalProfile] SUCCESS - Profile deleted');
    } catch (error) {
      console.error('[ProfessionalProfileService.deleteProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  // ===== USER OPERATIONS =====

  async getUserProfessionalProfile(userId: string): Promise<GetProfessionalProfileData | null> {
    console.log('[ProfessionalProfileService.getUserProfessionalProfile] START - UserId:', userId);
    try {
      const profile = await this.professionalProfileUseCase.getProfessionalProfileByUserId(userId);
      console.log('[ProfessionalProfileService.getUserProfessionalProfile] SUCCESS - Profile found:', !!profile);
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.getUserProfessionalProfile] ERROR:', error);
      throw error;
    }
  }

  async createOrUpdateUserProfile(userId: string, profileData: CreateProfessionalProfileData | UpdateProfessionalProfileData): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.createOrUpdateUserProfile] START - UserId:', userId);
    try {
      const existingProfile = await this.professionalProfileUseCase.getProfessionalProfileByUserId(userId);

      if (existingProfile) {
        // Update existing profile
        const updatedProfile = await this.professionalProfileUseCase.updateProfessionalProfile(
          existingProfile.id, 
          profileData as UpdateProfessionalProfileData
        );
        console.log('[ProfessionalProfileService.createOrUpdateUserProfile] SUCCESS - Profile updated');
        return updatedProfile;
      } else {
        // Create new profile
        const newProfile = await this.professionalProfileUseCase.createProfessionalProfile({
          userId: userId,
          ...profileData
        } as CreateProfessionalProfileData);
        console.log('[ProfessionalProfileService.createOrUpdateUserProfile] SUCCESS - Profile created');
        return newProfile;
      }
    } catch (error) {
      console.error('[ProfessionalProfileService.createOrUpdateUserProfile] ERROR:', error);
      throw error;
    }
  }

  // ===== SEARCH AND DISCOVERY =====

  async searchProfessionalsBySpecialty(specialty: string, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.searchProfessionalsBySpecialty] START - Specialty:', specialty, 'Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.searchProfessionalsBySpecialty(specialty, limitCount);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'professionalsSearched',
          // eventData: {
            // searchType: 'specialty',
            // searchTerm: specialty,
            // resultsCount: profiles.length
          // }
        // });
      }

      console.log('[ProfessionalProfileService.searchProfessionalsBySpecialty] SUCCESS - Found', profiles.length, 'professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.searchProfessionalsBySpecialty] ERROR:', error);
      throw error;
    }
  }

  async getAvailableProfessionals(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getAvailableProfessionals] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.getAvailableProfessionals(limitCount);
      console.log('[ProfessionalProfileService.getAvailableProfessionals] SUCCESS - Found', profiles.length, 'available professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.getAvailableProfessionals] ERROR:', error);
      throw error;
    }
  }

  async getVerifiedProfessionals(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getVerifiedProfessionals] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.getVerifiedProfessionals(limitCount);
      console.log('[ProfessionalProfileService.getVerifiedProfessionals] SUCCESS - Found', profiles.length, 'verified professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.getVerifiedProfessionals] ERROR:', error);
      throw error;
    }
  }

  async searchProfessionals(searchParams: {
    specialty?: string;
    location?: string;
    minExperience?: number;
    maxHourlyRate?: number;
    minHourlyRate?: number;
    language?: string;
    availableForRequests?: boolean;
  }, limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.searchProfessionals] START - SearchParams:', searchParams, 'Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.searchProfessionals(searchParams, limitCount);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'professionalsSearched',
          // eventData: {
            // searchType: 'advanced',
            // searchParams: searchParams,
            // resultsCount: profiles.length
          // }
        // });
      }

      console.log('[ProfessionalProfileService.searchProfessionals] SUCCESS - Found', profiles.length, 'matching professionals');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.searchProfessionals] ERROR:', error);
      throw error;
    }
  }

  async getFeaturedProfessionals(limitCount: number = 10): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getFeaturedProfessionals] START - Limit:', limitCount);
    try {
      // Get verified professionals with high ratings
      const allVerified = await this.professionalProfileUseCase.getVerifiedProfessionals();
      
      // Sort by rating and request count
      const featured = allVerified
        .filter(profile => profile.averageRating && profile.averageRating >= 4.5)
        .sort((a, b) => {
          // Primary sort by rating, secondary by request count
          const ratingDiff = (b.averageRating || 0) - (a.averageRating || 0);
          if (ratingDiff !== 0) return ratingDiff;
          return (b.totalRequests || 0) - (a.totalRequests || 0);
        })
        .slice(0, limitCount);

      console.log('[ProfessionalProfileService.getFeaturedProfessionals] SUCCESS - Found', featured.length, 'featured professionals');
      return featured;
    } catch (error) {
      console.error('[ProfessionalProfileService.getFeaturedProfessionals] ERROR:', error);
      throw error;
    }
  }

  // ===== VERIFICATION MANAGEMENT =====

  async submitProfileForVerification(profileId: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.submitProfileForVerification] START - ProfileId:', profileId);
    try {
      const profile = await this.professionalProfileUseCase.submitProfileForVerification(profileId);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'profileSubmittedForVerification',
          // eventData: {
            // profileId: profileId,
            // specialties: profile.specialties,
            // yearsOfExperience: profile.yearsOfExperience
          // },
          // userId: profile.userId
        // });
      }

      console.log('[ProfessionalProfileService.submitProfileForVerification] SUCCESS - Profile submitted for verification');
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.submitProfileForVerification] ERROR:', error);
      throw error;
    }
  }

  async verifyProfile(profileId: string, adminUserId?: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.verifyProfile] START - ProfileId:', profileId);
    try {
      const profile = await this.professionalProfileUseCase.verifyProfile(profileId);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'profileVerified',
          // eventData: {
            // profileId: profileId,
            // verifiedBy: adminUserId,
            // specialties: profile.specialties
          // },
          // userId: adminUserId
        // });
      }

      console.log('[ProfessionalProfileService.verifyProfile] SUCCESS - Profile verified');
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.verifyProfile] ERROR:', error);
      throw error;
    }
  }

  async rejectProfileVerification(profileId: string, reason?: string, adminUserId?: string): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.rejectProfileVerification] START - ProfileId:', profileId, 'Reason:', reason);
    try {
      const profile = await this.professionalProfileUseCase.rejectProfileVerification(profileId, reason);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'profileVerificationRejected',
          // eventData: {
            // profileId: profileId,
            // rejectionReason: reason,
            // rejectedBy: adminUserId
          // },
          // userId: adminUserId
        // });
      }

      console.log('[ProfessionalProfileService.rejectProfileVerification] SUCCESS - Profile verification rejected');
      return profile;
    } catch (error) {
      console.error('[ProfessionalProfileService.rejectProfileVerification] ERROR:', error);
      throw error;
    }
  }

  async getProfilesPendingVerification(limitCount?: number): Promise<GetProfessionalProfileData[]> {
    console.log('[ProfessionalProfileService.getProfilesPendingVerification] START - Limit:', limitCount);
    try {
      const profiles = await this.professionalProfileUseCase.getProfilesPendingVerification(limitCount);
      console.log('[ProfessionalProfileService.getProfilesPendingVerification] SUCCESS - Found', profiles.length, 'pending profiles');
      return profiles;
    } catch (error) {
      console.error('[ProfessionalProfileService.getProfilesPendingVerification] ERROR:', error);
      throw error;
    }
  }

  // ===== STATUS MANAGEMENT =====

  async updateAvailabilityStatus(profileId: string, isAvailable: boolean): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.updateAvailabilityStatus] START - ProfileId:', profileId, 'IsAvailable:', isAvailable);
    try {
      const updatedProfile = await this.professionalProfileUseCase.updateAvailabilityStatus(profileId, isAvailable);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'availabilityStatusChanged',
          // eventData: {
            // profileId: profileId,
            // isAvailable: isAvailable
          // },
          // userId: updatedProfile.userId
        // });
      }

      console.log('[ProfessionalProfileService.updateAvailabilityStatus] SUCCESS - Availability status updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileService.updateAvailabilityStatus] ERROR:', error);
      throw error;
    }
  }

  async updateStudentAcceptanceStatus(profileId: string, isAcceptingStudents: boolean): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.updateStudentAcceptanceStatus] START - ProfileId:', profileId, 'IsAcceptingStudents:', isAcceptingStudents);
    try {
      const updatedProfile = await this.professionalProfileUseCase.updateStudentAcceptanceStatus(profileId, isAcceptingStudents);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'studentAcceptanceStatusChanged',
          // eventData: {
            // profileId: profileId,
            // isAcceptingStudents: isAcceptingStudents
          // },
          // userId: updatedProfile.userId
        // });
      }

      console.log('[ProfessionalProfileService.updateStudentAcceptanceStatus] SUCCESS - Student acceptance status updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileService.updateStudentAcceptanceStatus] ERROR:', error);
      throw error;
    }
  }

  async updateProfileVisibility(profileId: string, visibility: 'public' | 'private' | 'draft'): Promise<GetProfessionalProfileData> {
    console.log('[ProfessionalProfileService.updateProfileVisibility] START - ProfileId:', profileId, 'Visibility:', visibility);
    try {
      const updatedProfile = await this.professionalProfileUseCase.updateProfileVisibility(profileId, visibility);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'profileVisibilityChanged',
          // eventData: {
            // profileId: profileId,
            // visibility: visibility
          // },
          // userId: updatedProfile.userId
        // });
      }

      console.log('[ProfessionalProfileService.updateProfileVisibility] SUCCESS - Profile visibility updated');
      return updatedProfile;
    } catch (error) {
      console.error('[ProfessionalProfileService.updateProfileVisibility] ERROR:', error);
      throw error;
    }
  }

  // ===== RATING AND REQUEST COUNT =====

  async addProfileReview(profileId: string, rating: number, reviewerId?: string): Promise<void> {
    console.log('[ProfessionalProfileService.addProfileReview] START - ProfileId:', profileId, 'Rating:', rating);
    try {
      await this.professionalProfileUseCase.addProfileReview(profileId, rating);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'professionalProfileReviewed',
          // eventData: {
            // profileId: profileId,
            // rating: rating,
            // reviewerId: reviewerId
          // },
          // userId: reviewerId
        // });
      }

      console.log('[ProfessionalProfileService.addProfileReview] SUCCESS - Profile review added');
    } catch (error) {
      console.error('[ProfessionalProfileService.addProfileReview] ERROR:', error);
      throw error;
    }
  }

  async recordRequest(profileId: string, _requestId?: string): Promise<void> {
    console.log('[ProfessionalProfileService.recordRequest] START - ProfileId:', profileId);
    try {
      await this.professionalProfileUseCase.recordRequest(profileId);

      // Track analytics event
      if (this.analyticsUseCase) {
        // await this.analyticsUseCase.trackEvent({
          // eventType: 'professionalRequestRecorded',
          // eventData: {
            // profileId: profileId,
            // requestId: requestId
          // }
        // });
      }

      console.log('[ProfessionalProfileService.recordRequest] SUCCESS - Request recorded');
    } catch (error) {
      console.error('[ProfessionalProfileService.recordRequest] ERROR:', error);
      throw error;
    }
  }

  // ===== ANALYTICS AND REPORTING =====

  async getProfessionalProfileStats(): Promise<{
    totalProfiles: number;
    verifiedProfiles: number;
    pendingVerification: number;
    availableForRequests: number;
    averageRating: number;
    topSpecialties: Array<{ specialty: string; count: number }>;
  }> {
    console.log('[ProfessionalProfileService.getProfessionalProfileStats] START');
    try {
      const allProfiles = await this.professionalProfileUseCase.getAllProfessionalProfiles();
      
      const totalProfiles = allProfiles.length;
      const verifiedProfiles = allProfiles.filter(p => p.verificationStatus === 'verified').length;
      const pendingVerification = allProfiles.filter(p => p.verificationStatus === 'pending').length;
      const availableForRequests = allProfiles.filter(p => p.isAvailable).length;
      
      const ratingsSum = allProfiles
        .filter(p => p.averageRating)
        .reduce((sum, p) => sum + (p.averageRating || 0), 0);
      const ratingsCount = allProfiles.filter(p => p.averageRating).length;
      const averageRating = ratingsCount > 0 ? ratingsSum / ratingsCount : 0;

      // Calculate top specialties
      const specialtyCount = new Map<string, number>();
      allProfiles.forEach(profile => {
        profile.specialties.forEach(specialty => {
          specialtyCount.set(specialty, (specialtyCount.get(specialty) || 0) + 1);
        });
      });

      const topSpecialties = Array.from(specialtyCount.entries())
        .map(([specialty, count]) => ({ specialty, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const stats = {
        totalProfiles,
        verifiedProfiles,
        pendingVerification,
        availableForRequests,
        averageRating,
        topSpecialties
      };

      console.log('[ProfessionalProfileService.getProfessionalProfileStats] SUCCESS - Stats calculated');
      return stats;
    } catch (error) {
      console.error('[ProfessionalProfileService.getProfessionalProfileStats] ERROR:', error);
      throw error;
    }
  }
}